import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';

import Alert from '@components/Alert';
import {
  PatientPlanList,
  PatientSeatsSummary,
  createPatientPlansSchema,
  getEligibleFeatures,
  sumAllocatedSeats,
} from '@components/PatientPlans';
import type { PatientPlanValues } from '@components/PatientPlans';
import RoundedButton from '@components/RoundedButton/index';
import SkeletonLoader from '@components/SkeletonLoader';

import { useListFeatureCatalog } from 'generated/custom-plan.generated';

import WizardStepIndicator from './WizardStepIndicator';

const messages = defineMessages({
  title: {
    id: 'newAccount.patientPlans.title',
    defaultMessage: 'Create patient plans for this organization',
  },
  intro: {
    id: 'newAccount.patientPlans.intro',
    defaultMessage:
      'Patient plans control what patients of this organization can access. Only features enabled for the organization can be included.',
  },
  availableCount: {
    id: 'newAccount.patientPlans.availableCount',
    defaultMessage:
      '{count} patient features available from the org features you enabled.',
  },
  seatPoolExceeded: {
    id: 'newAccount.patientPlans.seatPoolExceeded',
    defaultMessage:
      'Plans allocate {allocated} seats, more than the {total} in the organization’s pool.',
  },
  submit: {
    id: 'GLOBAL.next',
    defaultMessage: 'Next',
  },
  back: {
    id: 'GLOBAL.back',
    defaultMessage: 'Back',
  },
  catalogError: {
    id: 'newAccount.featureCatalogError',
    defaultMessage: 'Could not load the feature list',
  },
  catalogErrorBody: {
    id: 'newAccount.featureCatalogError.body',
    defaultMessage:
      'Organization features cannot be selected until this loads. Your other entries have been kept.',
  },
  retry: {
    id: 'GLOBAL.retry',
    defaultMessage: 'Retry',
  },
});

const BUTTON_HEIGHT = 44;

interface PatientPlansProps {
  initialValues: PatientPlanValues;
  /** The organization's patient seat pool, entered on the licence step */
  limitPatients: number | '';
  /** Org features enabled on the licence step */
  orgFeatureIds: string[];
  onSubmit?: (values: PatientPlanValues) => void;
  /**
   * Handed the plans as they stand, so returning to the licence step and coming
   * back does not discard work in progress.
   */
  onBack?: (values: PatientPlanValues) => void;
}

function PatientPlansForm({
  initialValues,
  limitPatients,
  orgFeatureIds,
  onSubmit,
  onBack,
}: PatientPlansProps) {
  const {
    data: catalogData,
    error: catalogError,
    loading: catalogLoading,
    refetch: refetchCatalog,
  } = useListFeatureCatalog();
  const { formatMessage } = useIntl();
  const totalSeats = Number(limitPatients) || 0;
  const eligibleFeatures = getEligibleFeatures(
    catalogData?.listFeatureCatalog ?? [],
    orgFeatureIds
  );

  // An empty list is indistinguishable from "this org has no features", so a
  // failed fetch must not be committable as plans with nothing included.
  const catalogFailed = Boolean(catalogError);

  const validationSchema: Yup.Schema<PatientPlanValues> = Yup.object().shape({
    plans: createPatientPlansSchema(
      { formatMessage },
      {
        totalSeats,
        seatPoolMessage: (allocated, total) =>
          formatMessage(messages.seatPoolExceeded, { allocated, total }),
      }
    ),
  });

  /** The number inputs hand Formik strings; the wizard state wants numbers. */
  function withNumericSeats(values: PatientPlanValues): PatientPlanValues {
    return {
      plans: values.plans.map((plan) => ({
        ...plan,
        seats: Number(plan.seats),
      })),
    };
  }

  async function handleSubmit(values: PatientPlanValues) {
    onSubmit?.(withNumericSeats(values));
  }

  // A feature disabled on a later visit to the licence step must not linger in
  // a plan, where it would still be counted and submitted.
  const eligibleIds = new Set(
    eligibleFeatures.map(({ feature_id }) => feature_id)
  );
  // Same reason `catalogFailed` blocks submit: an empty catalog is not evidence
  // that a plan's features are gone. Pruning against it would also change
  // `seededValues`, and `enableReinitialize` would reset away whatever the
  // admin typed during the error state when Retry succeeds.
  const seededValues: PatientPlanValues = catalogFailed
    ? initialValues
    : {
        plans: initialValues.plans.map((plan) => ({
          ...plan,
          feature_ids: plan.feature_ids.filter((id) => eligibleIds.has(id)),
        })),
      };

  return (
    <SkeletonLoader loading={catalogLoading}>
      <Formik
        enableReinitialize
        initialValues={seededValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, values }) => {
          const allocated = sumAllocatedSeats(values.plans);
          // Read from the values rather than from `errors.plans`: Formik writes
          // per-plan errors over the array-level message with `setIn`, so the
          // seat-pool string disappears exactly when a plan is also invalid.
          const seatPoolError =
            allocated > totalSeats
              ? formatMessage(messages.seatPoolExceeded, {
                  allocated,
                  total: totalSeats,
                })
              : '';

          return (
            <Form>
              <Stack gap={2.25}>
                <Stack
                  alignItems="flex-start"
                  direction="row"
                  flexWrap="wrap"
                  gap={2.5}
                  justifyContent="space-between"
                >
                  <Stack flex={1} gap={1} minWidth={280}>
                    <Typography color="text.secondary" variant="body1">
                      {formatMessage(messages.intro)}
                    </Typography>
                    <Typography
                      color="text.primary"
                      data-testid="patient-features-available"
                      variant="body2"
                    >
                      {formatMessage(messages.availableCount, {
                        count: eligibleFeatures.length,
                      })}
                    </Typography>
                  </Stack>
                  <PatientSeatsSummary
                    allocated={allocated}
                    total={totalSeats}
                  />
                </Stack>

                {catalogFailed && (
                  <Alert
                    action={
                      <RoundedButton
                        autoSize
                        height={32}
                        margin={0}
                        onClick={() => refetchCatalog()}
                        variant="white"
                      >
                        {formatMessage(messages.retry)}
                      </RoundedButton>
                    }
                    data-testid="feature-catalog-error"
                    title={formatMessage(messages.catalogError)}
                  >
                    {formatMessage(messages.catalogErrorBody)}
                  </Alert>
                )}

                <PatientPlanList
                  disabled={isSubmitting}
                  eligibleFeatures={eligibleFeatures}
                  plans={values.plans}
                />

                {seatPoolError && (
                  <Box>
                    <Typography
                      color="error"
                      data-testid="seat-pool-error"
                      variant="body2"
                    >
                      {seatPoolError}
                    </Typography>
                  </Box>
                )}

                <Stack gap={1.5}>
                  <RoundedButton
                    disabled={isSubmitting || catalogFailed}
                    height={BUTTON_HEIGHT}
                    margin={0}
                    type="submit"
                    width="100%"
                  >
                    {formatMessage(messages.submit)}
                  </RoundedButton>
                  {onBack && (
                    <RoundedButton
                      disabled={isSubmitting}
                      height={BUTTON_HEIGHT}
                      margin={0}
                      onClick={() => onBack(withNumericSeats(values))}
                      type="button"
                      width="100%"
                    >
                      {formatMessage(messages.back)}
                    </RoundedButton>
                  )}
                </Stack>
              </Stack>
            </Form>
          );
        }}
      </Formik>
    </SkeletonLoader>
  );
}

export default function PatientPlans(props: PatientPlansProps) {
  const { formatMessage } = useIntl();
  return (
    <Card>
      <CardHeader
        action={<WizardStepIndicator currentStep={1} />}
        title={formatMessage(messages.title)}
        titleTypographyProps={{
          variant: 'h6',
          color: 'textPrimary',
          component: 'h2',
        }}
      />
      <Divider />
      <CardContent>
        <Stack gap={1}>
          <PatientPlansForm {...props} />
        </Stack>
      </CardContent>
    </Card>
  );
}
